import React, { useState, useEffect } from 'react';
import {
  X, Instagram, Facebook, Linkedin, Youtube,
  ShieldCheck, AlertCircle, Info, ChevronRight, Check,
  BookOpen, ExternalLink, Key
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter
} from "@/ui/dialog";
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import API from '@/api/api01';
import { toast } from 'sonner';
import { cn } from '@/ui/utils';

interface ConnectIntegrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialPlatform?: string;
}

const SETUP_GUIDES: Record<string, { title: string; steps: string[]; link: string }> = {
  Instagram: {
    title: 'Instagram API Setup',
    steps: [
      'Log into Meta for Developers portal.',
      'Create a new App (Type: Business).',
      'Add "Instagram Graph API" to your app.',
      'Go to "Tools" -> "Graph API Explorer".',
      'Select your App and generate a User Access Token.',
      'Exchange it for a "Long-Lived Token" in Settings.'
    ],
    link: 'https://developers.facebook.com/docs/instagram-api'
  },
  Facebook: {
    title: 'Facebook Lead Ads Setup',
    steps: [
      'Visit Meta Business Suite Account Settings.',
      'Navigate to "Ad Accounts" and copy your ID.',
      'Go to "Security Center" and enable 2FA.',
      'In Developers Portal, App -> Dashboad -> Marketing API.',
      'Generate a Token with "ads_management" permissions.'
    ],
    link: 'https://www.facebook.com/business/help/397336587121938'
  },
  LinkedIn: {
    title: 'LinkedIn Ads Integration',
    steps: [
      'Open LinkedIn Campaign Manager.',
      'Select your Account -> Account Settings.',
      'Go to "Lead Gen Forms" -> "API Keys".',
      'Create a new API Key with "Lead Retrieval" access.',
      'Copy the Client ID and Secret Provided.'
    ],
    link: 'https://business.linkedin.com/marketing-solutions/ads/lead-gen-forms'
  },
  YouTube: {
    title: 'YouTube Google Ads Setup',
    steps: [
      'Sign in to your Google Ads account.',
      'Go to "Tools & Settings" -> "API Center".',
      'Request a "Developer Token" from Google.',
      'In Google Cloud Console, enable "Google Ads API".',
      'Generate an OAuth2 Client ID and Secret.'
    ],
    link: 'https://developers.google.com/google-ads/api/docs/start'
  }
};

export default function ConnectIntegrationModal({ open, onOpenChange, onSuccess, initialPlatform }: ConnectIntegrationModalProps) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showGuide, setShowGuide] = useState(false);
  const [platform, setPlatform] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    if (open) {
      setPlatform(initialPlatform || '');
      setStep(initialPlatform ? 2 : 1);
      setAccountName('');
      setAccountId('');
      setAccessToken('');
      setShowGuide(false);
    }
  }, [open, initialPlatform]);

  const PLATFORM_OPTIONS = [
    { id: 'Instagram', name: 'Instagram', icon: <Instagram className="text-[#E1306C]" />, desc: 'Ads & Leads' },
    { id: 'Facebook', name: 'Facebook', icon: <Facebook className="text-[#1877F2]" />, desc: 'Lead Forms' },
    { id: 'LinkedIn', name: 'LinkedIn', icon: <Linkedin className="text-[#0A66C2]" />, desc: 'Ads Manager' },
    { id: 'YouTube', name: 'YouTube', icon: <Youtube className="text-[#FF0000]" />, desc: 'Extensions' },
  ];

  const handleConnect = async () => {
    if (!accountName) {
      toast.error("Please provide an account name");
      return;
    }

    try {
      setLoading(true);
      await API.post('/superadmin/social-integrations', {
        platform,
        account_name: accountName,
        account_id: accountId,
        access_token: accessToken,
        status: 'Connected'
      });
      toast.success(`${platform} connected successfully!`);
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err) {
      toast.error("Failed to connect account.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPlatform('');
    setAccountName('');
    setAccountId('');
    setAccessToken('');
    setStep(1);
    setShowGuide(false);
  };

  const currentGuide = SETUP_GUIDES[platform];

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] h-auto p-0 overflow-hidden bg-white dark:bg-slate-900 backdrop-blur-2xl border border-slate-200 dark:border-slate-800 shadow-[0_50px_100px_rgba(0,0,0,0.4)] rounded-3xl transition-all duration-500">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600" />

        {/* Header */}
        <div className="p-8 pb-4 relative overflow-hidden shrink-0">
          <DialogHeader className="relative z-10 space-y-1">
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-[10px] font-bold uppercase tracking-wider border border-purple-500/20">
                {showGuide ? <BookOpen size={12} /> : <ShieldCheck size={12} />}
                {showGuide ? 'Documentation' : `Step ${step} of 2`}
              </div>
            </div>
            <DialogTitle className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
              {showGuide ? `${platform} Setup Guide` : (step === 1 ? 'Select Platform' : `Setup ${platform}`)}
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400 text-sm font-medium">
              {showGuide ? 'Follow these steps to obtain your API credentials.' : (step === 1 ? 'Choose the social network to sync.' : 'Link your business account credentials.')}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable Content Area */}
        <div className="p-8 pt-4 space-y-5 max-h-[60vh] overflow-y-auto scrollbar-hide">
          {showGuide ? (
            <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
              <div className="space-y-4">
                {currentGuide.steps.map((s, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold pointer-events-none text-slate-500 dark:text-slate-400 group-hover:bg-purple-500/10 group-hover:text-purple-600 transition-colors shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed pt-0.5">
                      {s}
                    </p>
                  </div>
                ))}
              </div>
              <a
                href={currentGuide.link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-purple-500/50 transition-all text-sm font-bold text-slate-700 dark:text-slate-200"
              >
                Official Documentation <ExternalLink size={14} />
              </a>
            </div>
          ) : (
            <>
              {step === 1 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PLATFORM_OPTIONS.map((opt) => (
                    <div
                      key={opt.id}
                      onClick={() => { setPlatform(opt.id); setStep(2); }}
                      className="group relative p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:bg-slate-100 dark:hover:bg-slate-900 transition-all cursor-pointer overflow-hidden flex flex-col items-center text-center hover:border-purple-500/50"
                    >
                      <div className="absolute top-4 right-4 h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ChevronRight size={12} className="text-white" />
                      </div>
                      <div className="h-12 w-12 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm">
                        {React.cloneElement(opt.icon as React.ReactElement, { size: 28 })}
                      </div>
                      <h4 className="font-bold text-slate-900 dark:text-white text-[15px]">{opt.name}</h4>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-1">{opt.desc}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-5 animate-in fade-in duration-300">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Account Display Name</Label>
                      <Input
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="Social Agency"
                        className="rounded-2xl h-12 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 focus:ring-purple-500/50 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Business ID</Label>
                      <Input
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        placeholder="Optional"
                        className="rounded-2xl h-12 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 focus:ring-purple-500/50 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between ml-1">
                      <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">Access Token / API Key</Label>
                      <button
                        onClick={() => setShowGuide(true)}
                        className="text-[10px] text-amber-500 font-bold hover:underline flex items-center gap-1 group"
                      >
                        How to obtain? <Info size={10} className="group-hover:rotate-12 transition-transform" />
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Key size={14} className="text-slate-400" />
                      </div>
                      <Input
                        type="password"
                        value={accessToken}
                        onChange={(e) => setAccessToken(e.target.value)}
                        placeholder="Paste token here"
                        className="rounded-2xl h-12 pl-10 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 focus:ring-purple-500/50 transition-all"
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 dark:bg-purple-500/10 rounded-2xl text-purple-700 dark:text-purple-300 flex gap-3 items-center border border-purple-100 dark:border-purple-500/20">
                    <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center shrink-0">
                      <Check size={14} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="text-[12px] font-medium">
                      Webhooks will securely push leads to your board instantly.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="p-8 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/80 backdrop-blur-md flex items-center gap-4">
          {showGuide ? (
            <Button variant="ghost" onClick={() => setShowGuide(false)} className="rounded-2xl h-12 px-8 font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 w-full">
              Back to Setup
            </Button>
          ) : (
            <>
              {step === 2 && !initialPlatform && (
                <Button variant="ghost" onClick={() => setStep(1)} className="rounded-2xl h-12 px-6 font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5">
                  Back
                </Button>
              )}
              <div className="flex-1" />
              <Button
                onClick={step === 2 ? handleConnect : undefined}
                disabled={loading || step === 1}
                className={cn(
                  "rounded-2xl h-12 px-10 font-black shadow-xl transition-all duration-200",
                  step === 2 ? "bg-purple-600 hover:bg-purple-700 text-white min-w-[200px]" : "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed hidden sm:flex"
                )}
              >
                {loading ? 'Wait...' : 'Connect Channel'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
